import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Truck, Calendar, Wrench } from 'lucide-react';

const TruckCard = ({ truck, onSelect, onEdit, onMaintenance, onViewDocument, onVisibilityChange, isAdmin, language = 'pt-BR' }) => {
  const statusColors = {
    disponivel: 'bg-green-50 border-green-200 text-green-700',
    alugado: 'bg-blue-50 border-blue-200 text-blue-700',
    reservado: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    manutencao: 'bg-orange-50 border-orange-200 text-orange-700'
  };

  const statusText = {
    disponivel: language === 'pt-BR' ? 'Disponível' : 'Available',
    alugado: language === 'pt-BR' ? 'Em Uso' : 'In Use',
    reservado: language === 'pt-BR' ? 'Reservado' : 'Reserved',
    manutencao: language === 'pt-BR' ? 'Manutenção' : 'Maintenance'
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="aspect-video bg-gray-200 rounded-lg mb-3 flex items-center justify-center overflow-hidden">
          {truck.fotos && truck.fotos.length > 0 && !truck.fotos[0].includes('blob:') ? (
            <img 
              src={truck.fotos[0]} 
              alt={truck.modelo} 
              className="w-full h-full object-cover rounded-lg"
              onError={(e) => {
                e.target.style.display = 'none';
                const placeholder = e.target.parentElement.querySelector('.placeholder-truck');
                if (placeholder) placeholder.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className="placeholder-truck flex-col items-center justify-center w-full h-full" 
            style={{ display: (truck.fotos?.length > 0 && !truck.fotos[0].includes('blob:')) ? 'none' : 'flex' }}
          >
            <Truck className="h-16 w-16 text-gray-400" />
            <p className="text-xs text-gray-500 mt-2">{language === 'pt-BR' ? 'Sem foto' : 'No photo'}</p>
          </div>
        </div>

        <h3 className="font-bold text-lg">{truck.modelo}</h3>
        <p className="text-sm text-gray-600">{truck.ano} • {truck.cor}</p>
        <p className="text-sm font-mono text-gray-700 mt-1">📍 {truck.placa}</p>
        {truck.vin_number && (
          <p className="text-xs font-mono text-gray-500 mt-1">🔖 VIN: {truck.vin_number}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">🛣️ {truck.quilometragem_atual?.toLocaleString()} km</p>

        <div className={`mt-3 px-3 py-1 rounded-full text-sm font-semibold text-center ${statusColors[truck.status]}`}>
          {statusText[truck.status]}
        </div>

        {isAdmin && (
          <>
            {/* Toggle Visibilidade */}
            <div className="flex items-center justify-center gap-2 mt-3 p-2 bg-zinc-50 rounded-lg border border-zinc-200">
              <span className="text-xs text-zinc-600">
                {truck.visible_to_pms !== false ? (language === 'pt-BR' ? 'Visível' : 'Visible') : (language === 'pt-BR' ? 'Oculto' : 'Hidden')}
              </span>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!onVisibilityChange) return;
                  
                  try {
                    const token = sessionStorage.getItem('access_token');
                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                    const newValue = truck.visible_to_pms === false;
                    
                    await fetch(`${API_URL}/api/trucks/${truck.id}/visibility`, {
                      method: 'PUT',
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({ visible: newValue })
                    });
                    
                    // Chamar callback para atualizar lista
                    onVisibilityChange();
                  } catch (error) {
                    console.error('Erro:', error);
                  }
                }}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  truck.visible_to_pms !== false ? 'bg-emerald-500' : 'bg-zinc-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  truck.visible_to_pms !== false ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
            
            <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={() => onEdit(truck)} className="flex-1">
              ✏️ {language === 'pt-BR' ? 'Editar' : 'Edit'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => onMaintenance(truck)}>
              <Wrench className="h-4 w-4" />
            </Button>
            <Button 
              size="sm" 
              variant="destructive" 
              onClick={async () => {
                if (window.confirm(language === 'pt-BR' 
                  ? `Deletar caminhão ${truck.placa}? Esta ação não pode ser desfeita.`
                  : `Delete truck ${truck.placa}? This action cannot be undone.`
                )) {
                  try {
                    const token = sessionStorage.getItem('access_token');
                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                    await fetch(`${API_URL}/api/trucks/${truck.id}`, {
                      method: 'DELETE',
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    window.location.reload();
                  } catch (error) {
                    alert(language === 'pt-BR' ? 'Erro ao deletar' : 'Delete error');
                  }
                }
              }}
              title={language === 'pt-BR' ? 'Deletar' : 'Delete'}
            >
              🗑️
            </Button>
          </div>
          </>
        )}

        {/* Botões Ver Documentos */}
        <div className="flex gap-2 mt-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => onViewDocument && onViewDocument('registration', truck)}
            className="flex-1 text-xs"
            disabled={!truck.documentos?.registration?.url}
          >
            📄 Registration
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => onViewDocument && onViewDocument('seguro', truck)}
            className="flex-1 text-xs"
            disabled={!truck.documentos?.seguro?.url}
          >
            🛡️ {language === 'pt-BR' ? 'Seguro' : 'Insurance'}
          </Button>
        </div>

        {!isAdmin && truck.status === 'disponivel' && (
          <Button onClick={() => onSelect(truck)} className="w-full mt-3 bg-green-600 hover:bg-green-700">
            {language === 'pt-BR' ? '➕ Solicitar' : '➕ Request'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default TruckCard;